import React, { useState, useEffect, useCallback } from 'react';
import { Store, Check, X, Clock, MapPin, Phone, Globe } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminBusinessApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching applications from:', `${API_URL}/business/applications?status=${filter}`);

      const response = await axios.get(
        `${API_URL}/business/applications?status=${filter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('✅ Received response:', {
        success: response.data.success,
        count: response.data.count,
        applications: response.data.data
      });

      if (!response.data.data || !Array.isArray(response.data.data)) {
        console.error('❌ Invalid data format:', response.data);
        setError('Invalid data format received from server');
        setApplications([]);
      } else {
        setApplications(response.data.data);
      }
    } catch (err) {
      console.error('❌ Error fetching applications:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to fetch applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this business account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/business/approve/${userId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess('Business account approved successfully!');
      fetchApplications();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve application');
    }
  };

  const handleReject = async () => {
    if (!selectedApp || !rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/business/reject/${selectedApp._id}`,
        { reason: rejectionReason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess('Business application rejected');
      setShowRejectModal(false);
      setSelectedApp(null);
      setRejectionReason('');
      fetchApplications();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject application');
    }
  };

  const openRejectModal = (app) => {
    setSelectedApp(app);
    setShowRejectModal(true);
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading applications...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <h1 style={{ margin: 0 }}>Business Applications</h1>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setFilter('pending')}
            className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
          >
            <Clock size={16} style={{ marginRight: '4px' }} />
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`btn ${filter === 'approved' ? 'btn-primary' : 'btn-outline-primary'}`}
          >
            <Check size={16} style={{ marginRight: '4px' }} />
            Approved
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`btn ${filter === 'rejected' ? 'btn-primary' : 'btn-outline-primary'}`}
          >
            <X size={16} style={{ marginRight: '4px' }} />
            Rejected
          </button>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {applications.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p className="text-muted">No {filter} applications found</p>
          <p style={{ fontSize: '12px', color: 'var(--textSecondary)', marginTop: '8px' }}>
            Check browser console for debug information
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {applications.map((app) => (
            <div key={app._id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Store size={32} color="var(--primary)" />
                  <div>
                    <h3 style={{ margin: 0, marginBottom: '4px' }}>{app.businessInfo?.businessName}</h3>
                    <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                      Applied by: {app.username} ({app.email})
                    </div>
                  </div>
                </div>

                {filter === 'pending' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApprove(app._id)}
                      className="btn"
                      style={{ backgroundColor: '#10B981', color: 'white', borderColor: '#10B981' }}
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => openRejectModal(app)}
                      className="btn"
                      style={{ backgroundColor: '#EF4444', color: 'white', borderColor: '#EF4444' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                    Business Info
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {app.businessInfo?.taxId && <div>Tax ID: {app.businessInfo.taxId}</div>}
                    {app.businessInfo?.businessLicense && <div>License: {app.businessInfo.businessLicense}</div>}
                    {app.businessInfo?.website && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Globe size={14} />
                        <a href={app.businessInfo.website} target="_blank" rel="noopener noreferrer">
                          Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                    Store Location
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={14} />
                      {app.storeLocation?.city}, {app.storeLocation?.state}
                    </div>
                    <div>{app.storeLocation?.street}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Phone size={14} />
                      {app.storeLocation?.phone}
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                    Store Hours
                  </div>
                  <div style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    {app.storeLocation?.storeHours}
                  </div>
                </div>
              </div>

              {app.businessInfo?.description && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                    Description
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                    {app.businessInfo.description}
                  </div>
                </div>
              )}

              {app.storeLocation?.pickupInstructions && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                    Pickup Instructions
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                    {app.storeLocation.pickupInstructions}
                  </div>
                </div>
              )}

              {app.businessRejectionReason && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#FEE2E2', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    Rejection Reason:
                  </div>
                  <div style={{ fontSize: '14px' }}>{app.businessRejectionReason}</div>
                </div>
              )}

              {app.businessApprovedAt && (
                <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--textSecondary)' }}>
                  Approved on {new Date(app.businessApprovedAt).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '16px' }}>Reject Application</h3>
            <p className="text-muted" style={{ marginBottom: '16px' }}>
              Please provide a reason for rejecting this business application:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="input"
              rows="4"
              placeholder="Enter rejection reason..."
              style={{ marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedApp(null);
                  setRejectionReason('');
                }}
                className="btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="btn"
                style={{ backgroundColor: '#EF4444', color: 'white', borderColor: '#EF4444' }}
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessApplications;
